import { useContext, useEffect, useState } from "react";
import {
  getApplications,
  createApplications,
  updateApplications,
  deleteApplication,
  getApplicationByTenant
} from "../services/console/application";
import { AppContext } from "../context";

type UseType = "DATA" | "ID" | "ACTIONS" | "TENANTID" | "APPS";
type ActionType = "CREATE" | "UPDATE" | "DELETE" | "REACTIVE";

const handleApplicationActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createApplications,
        fnMsg: t("Applications.apiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateApplications,
        fnMsg: t("Applications.apiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteApplication,
        fnMsg: t("Applications.apiDeleteLabel"),
      };
    case "REACTIVE":
      return {
        actionFn: updateApplications,
        fnMsg: t("Applications.apiReactiveLabel"),
      };
    default:
      return {
        actionFn: () => { },
        fnMsg: "",
      };
  }
};

export const useApplications = (type: UseType, filterTenantId?: string) => {
  const {
    t,
    toast,
    appState: { selectedApplication, selectedTenantId, tenantIdListsNavigations, tenantId },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApplication?.id || "0";
  const tenantid = selectedTenantId || tenantId || "0";
  const selectTenant = selectedTenantId || "0";

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const applicationAction = async (action: ActionType, data: any, nameObject?: string) => {
    const { actionFn, fnMsg } = handleApplicationActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data))
      .then((_) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${!!nameObject ? fnMsg.replace('{name}',nameObject) : fnMsg.replace('"{name}" ','').replace(':','')}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type !== "ACTIONS" && type !== "TENANTID" && type !== "APPS" && appid) {
      (async function () {
        setAppState((prev) => ({
          ...prev,
          isLoading: type === "ID",
        }));
        await Promise.resolve(getApplications(type === "ID" && appid, filterTenantId || tenantid))
          .then((response) => {
            let appData;
            if (type === "ID") {
              appData = {
                id: response?.data?.id,
                name: response?.data?.name,
                segment: response?.data?.segment,
                currentversion: response?.data?.currentversion,
                installedversion: response?.data?.installedversion,
                status: response?.data?.status,
                createdby: response?.data?.createdby,
                tenantid: response?.data?.tenantid,
                updatedat: response?.data?.updatedat,
              };
            } else {
              appData = response?.data.map((app: any) => ({
                id: app?.id,
                name: app?.name,
                segment: app?.segment,
                currentversion: app?.currentversion,
                installedversion: app?.installedversion,
                status: app?.status,
                createdby: app?.createdby,
                tenantid: app?.tenantid,
                updatedat: app?.updatedat,
                createdat: app?.createdat,
              }));
            }
            setData(appData);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
        setAppState((prev) => ({
          ...prev,
          isLoading: type === "ID",
        }));
      })();
    }
  }, [appid, tenantid, toast, reload, type, setAppState]);

  useEffect(() => {
    if (type === "TENANTID" && selectTenant) {
      (async function () {
        await Promise.resolve(getApplicationByTenant(selectTenant))
          .then((response) => {
            setData(response?.data);
            setAppState((prev) => ({
              ...prev,
              tenantParentName: response?.data[0]?.name,
              tenantIdListsNavigations: [...new Set([...tenantIdListsNavigations, tenantid])]
            }));
          })
          .catch((error) => {
            setData(null);
          });
      })();
    }
  }, [selectTenant, toast, type, reload]);

  const getInfoApplicationsByTenant = async (tenantid: string) => {
    if (type === "APPS") {
      return await Promise.resolve(getApplicationByTenant(tenantid))
        .then(async (response) => {
          setAppState((prev) => ({
            ...prev,
            hasApplications: true
          }));
          return await response?.data;
        })
        .catch((error) => {
          setAppState((prev) => ({
            ...prev,
            hasApplications: false
          }));
          return null;
        });
    }
  }

  return {
    data,
    applicationAction,
    reloadFn,
    getInfoApplicationsByTenant
  };
};
